import { Form, Spin } from "antd";
import styles from "./index.module.css";
import { useForm } from "antd/es/form/Form";
import { useRequest } from "ahooks";
import { useContext, useEffect, useRef } from "react";
import { NotificationContext } from "context/notificationContext";
import { AdminContext } from "context/adminContext";
import { getInsuranceRatioLimits, RateLimits, updateInsuranceRatioLimits } from "helpers/backend_helper";
import { ModalComponentV1 } from "Components/ModalComponentV1";
import { CustomButton } from "Components/CustomButton";
import { CustomInput } from "Components/CustomInput";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const RatioLimitModal = ({ isOpen, onClose }: Props) => {
  const [form] = useForm();
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const { localCountry } = useContext(AdminContext);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const { data: rateLimits, loading: isRateLimitsLoading } = useRequest(
    () => getInsuranceRatioLimits(localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry.iso],
      onError: (err: any) => {
        openNotificationWithIcon("error", err?.response?.data?.message || err.message);
      },
    },
  );

  useEffect(() => {
    if (rateLimits) {
      form.setFieldsValue(rateLimits);
    }
  }, [rateLimits, form]);
  const { run: updateRateLimits, loading: isPostRateLimitLoading } = useRequest(
    (limits: RateLimits) => updateInsuranceRatioLimits(limits, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry.iso],
      manual: true,
      onError: (err: any) => {
        openNotificationWithIcon("error", err?.response?.data?.message || err.message);
      },
      onSuccess: () => {
        openNotificationWithIcon("success", "Rate limit has been updated successfully");
        onClose();
      },
    },
  );
  const onFinish = (values: RateLimits) => {
    for (const prop in values) {
      if (values[prop] instanceof Object) {
        values[prop].min = +values[prop].min;
        values[prop].max = +values[prop].max;
      } else {
        values[prop] = +values[prop];
      }
    }
    updateRateLimits(values);
  };

  const validateMinMax = (rule: any, value: number) =>
    new Promise<void>((resolve, reject) => {
      clearTimeout(debounceTimers.current[rule.field]);
      debounceTimers.current[rule.field] = setTimeout(() => {
        const minValue = +form.getFieldValue([rule.field.split(".")[0], "min"]);
        const maxValue = +form.getFieldValue([rule.field.split(".")[0], "max"]);

        if (rule.field.endsWith("min")) {
          if (+value < 0.1) return reject("Min < 0.1");
          if (value > maxValue) return reject("Min > Max");
        } else if (rule.field.endsWith("max")) {
          if (+value > 5) return reject("Max > 5");
          if (value < minValue) return reject("Max < Min");
        }
        resolve();
      }, 500);
    });
  return (
    <ModalComponentV1 isOpen={isOpen} onClose={onClose} title="Ratio Limits" className={styles.modal}>
      <Form layout="vertical" form={form} className={styles.cascaderWrapper} onFinish={onFinish}>
        {isRateLimitsLoading ? (
          <Spin style={{ left: "50%", top: "50%", transform: "translate(50% -50%)", width: "100%", height: "100%" }} />
        ) : (
          <>
            {rateLimits &&
              Object.entries(rateLimits).map((v) => {
                if (v[1] instanceof Object)
                  return (
                    <Form.Item key={v[0]} style={{ margin: 0 }}>
                      <div className={styles.allWrapInfo}>
                        <span className={styles.labelOfInfo}>{v[0][0].toUpperCase() + v[0].slice(1)}</span>
                        <div className={styles.range}>
                          <Form.Item
                            name={[v[0], "min"]}
                            className={styles.formWrapperInput}
                            initialValue={v[1].min}
                            dependencies={[[v[0], "max"]]}
                            rules={[
                              { required: true, message: "This field is required" },
                              { validator: validateMinMax },
                              { pattern: /^\d*\.?\d*$/, message: "Invalid number" },
                            ]}
                          >
                            <CustomInput
                              className={`${styles.inputInRange} ${
                                form.getFieldError([v[0], "min"]).length > 0 ? styles.errorBorder : ""
                              }`}
                              suffix="min"
                              type="number"
                            />
                          </Form.Item>
                          <div style={{ marginTop: 9 }}>-</div>
                          <Form.Item
                            name={[v[0], "max"]}
                            className={styles.formWrapperInput}
                            initialValue={v[1].max}
                            dependencies={[[v[0], "min"]]}
                            rules={[
                              { required: true, message: "This field is required" },
                              { validator: validateMinMax },
                              { pattern: /^\d*\.?\d*$/, message: "Invalid number" },
                            ]}
                          >
                            <CustomInput
                              className={`${styles.inputInRange} ${
                                form.getFieldError([v[0], "max"]).length > 0 ? styles.errorBorder : ""
                              }`}
                              suffix="max"
                              type="number"
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Form.Item>
                  );

                return (
                  <Form.Item key={v[0]}>
                    <div className={styles.allWrapInfo}>
                      <span className={styles.labelOfInfo}>{v[0].toUpperCase()}</span>
                      <Form.Item
                        name={v[0]}
                        className={styles.formWrapperInput}
                        initialValue={v[1]}
                        rules={[
                          { required: true, message: "This field is required" },
                          { pattern: /^\d*\.?\d*$/, message: "Invalid number" },
                          {
                            validator: (_rule, value) =>
                              new Promise<void>((resolve, reject) => {
                                clearTimeout(debounceTimers.current[v[0]]);
                                debounceTimers.current[v[0]] = setTimeout(() => {
                                  if (+value < 0.1) return reject("Min < 0.1");
                                  if (+value > 5) return reject("Max > 5");
                                  resolve();
                                }, 500);
                              }),
                          },
                        ]}
                      >
                        <CustomInput
                          type="number"
                          className={styles.oneInput + " " + styles.inputInRange}
                          style={{ width: 347 }}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>
                );
              })}
            <div className={styles.btnWrapper}>
              <CustomButton
                sizeButton="large"
                typeButton="tertiary"
                onClick={onClose}
                disabled={isPostRateLimitLoading}
              >
                Cancel
              </CustomButton>
              <CustomButton sizeButton="large" typeButton="primary" htmlType="submit" disabled={isPostRateLimitLoading}>
                Update
              </CustomButton>
            </div>
          </>
        )}
      </Form>
    </ModalComponentV1>
  );
};
